import { useState, useEffect } from "react";
import { Pizza, ArrowRight, CheckCircle2, ShieldCheck, Upload, Package, Wrench } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { Trans, useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

const CATEGORY_SUGGESTIONS = {
  insumos: [
    "Lácteos", "Carnes", "Bebidas", "Empaques", "Desechables",
    "Abarrotes", "Verduras y frutas", "Limpieza", "Otros",
  ],
  servicios: [
    "Mantenimiento general", "Fumigación / Control de plagas", "A/C y refrigeración",
    "Vidrios y aluminio", "Electricidad", "Construcción / Obra civil",
    "Tratamiento de aguas / Trampa de grasa", "Extintores y seguridad",
    "Limpieza profesional", "Otros",
  ],
};

const MODALITY_OPTIONS = ["por_visita", "contrato_mensual", "por_evento", "cotizacion_individual"];
const RESPONSE_OPTIONS = ["24h", "48h", "72h", "semanal", "mensual"];

export default function SupplierRegistration() {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    supplier_type: "",
    business_name: "", rfc: "", tax_address: "",
    contact_email: "", contact_phone: "",
    bank_name: "", bank_account: "",
    credit_days: 0, category: "",
    service_modality: "", service_response_time: "",
    nda_accepted: false,
  });
  // doc_type -> { file, id, uploading }
  const [docs, setDocs] = useState({});
  const [privacyNotice, setPrivacyNotice] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [codeOfConduct, setCodeOfConduct] = useState(null);
  const [codeAccepted, setCodeAccepted] = useState(false);
  const [codeDocumentId, setCodeDocumentId] = useState(null);
  const [submittingCode, setSubmittingCode] = useState(false);

  useEffect(() => {
    api.get("/privacy-notice/current").then((r) => setPrivacyNotice(r.data)).catch(() => setPrivacyNotice(null));
    api.get("/supplier-code-of-conduct/current").then((r) => setCodeOfConduct(r.data)).catch(() => setCodeOfConduct(null));
  }, []);

  const isServicios = form.supplier_type === "servicios";

  const DOC_FIELDS = [
    { type: "csf", required: true },
    { type: "official_id", required: true },
    { type: "proof_of_address", required: true },
    { type: "bank_statement", required: true },
    { type: "sat_compliance", required: false },
    { type: "acta_constitutiva", required: false, hint: true },
    { type: "poder_legal", required: false, hint: true },
  ];

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleDocChange = async (docType, file) => {
    if (!file) return;
    const MAX_SIZE_MB = 15;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(t("supplierReg.docTooLargeMsg", { size: (file.size / 1024 / 1024).toFixed(1), max: MAX_SIZE_MB }));
      return;
    }
    const okTypes = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const okExts = ["pdf", "jpg", "jpeg", "png", "webp", "heic", "heif"];
    if (!okTypes.includes(file.type) && !okExts.includes(ext)) {
      toast.error(t("supplierReg.docBadTypeMsg", { name: file.name }));
      return;
    }
    setDocs((d) => ({ ...d, [docType]: { file, uploading: true } }));
    try {
      const data = await fileToDataUrl(file);
      const r = await api.post("/public/suppliers/upload-document", { doc_type: docType, filename: file.name, data });
      setDocs((d) => ({ ...d, [docType]: { file, id: r.data.id, uploading: false } }));
    } catch (e) {
      const backendDetail = e?.response?.data?.detail;
      toast.error(backendDetail
        ? t("supplierReg.docUploadErrorDetail", { name: file.name, detail: backendDetail })
        : t("supplierReg.docUploadErrorGeneric", { name: file.name }));
      setDocs((d) => { const n = { ...d }; delete n[docType]; return n; });
    }
  };

  const mandatoryUploaded = ["csf", "official_id", "proof_of_address", "bank_statement"].every((tp) => docs[tp]?.id);
  const serviceFieldsOk = !isServicios || (form.service_modality && form.service_response_time);

  const submit = async () => {
    if (!form.nda_accepted) {
      toast.error(t("supplierReg.ndaRequired"));
      return;
    }
    if (privacyNotice && !privacyAccepted) {
      toast.error(t("supplierReg.privacyRequired"));
      return;
    }
    if (codeOfConduct && !codeAccepted) {
      toast.error("Debes leer y aceptar el Código de Conducta para continuar.");
      return;
    }
    if (!serviceFieldsOk) {
      toast.error(t("supplierReg.serviceFieldsRequired"));
      setStep(3);
      return;
    }
    if (!mandatoryUploaded) {
      toast.error(t("supplierReg.docsRequired"));
      setStep(4);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/suppliers/register", {
        ...form,
        service_modality: isServicios ? form.service_modality : null,
        service_response_time: isServicios ? form.service_response_time : null,
        csf_document_id: docs.csf?.id,
        official_id_document_id: docs.official_id?.id,
        proof_of_address_document_id: docs.proof_of_address?.id,
        bank_statement_document_id: docs.bank_statement?.id,
        sat_compliance_document_id: docs.sat_compliance?.id || null,
        acta_constitutiva_document_id: docs.acta_constitutiva?.id || null,
        poder_legal_document_id: docs.poder_legal?.id || null,
        privacy_notice_id: privacyNotice?.id || null,
        code_of_conduct_id: codeOfConduct?.id || null,
      });
      setSuccess(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("supplierReg.registerError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 relative">
        <div className="absolute top-4 right-4 z-20">
          <LanguageSwitcher variant="topbar" />
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-3xl border border-slate-200 p-8 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" strokeWidth={2} />
          </div>
          <h1 className="font-display font-extrabold text-2xl text-[#1A1A1A] mt-5">{t("supplierReg.successTitle")}</h1>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed">
            <Trans i18nKey="supplierReg.successBody" values={{ name: form.business_name, email: form.contact_email }} components={{ strong: <strong className="text-[#1A1A1A]" /> }} />
          </p>
          <div className="mt-6 p-4 bg-[#FFFBE6] border border-[#FFDF00] rounded-xl text-left text-xs text-slate-700">
            <strong className="block mb-1 text-[#1A1A1A]">{t("supplierReg.nextSteps")}</strong>
            {t("supplierReg.step1")}<br />
            {t("supplierReg.step2")}<br />
            {t("supplierReg.step3")}
          </div>
          <a href="/login" data-testid="back-to-login"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#1A1A1A]">
            {t("supplierReg.goToLogin")} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
            <Pizza className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-[#1A1A1A] text-base">Chizy Chiz</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("supplierReg.headerTitle")}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher variant="topbar" />
          <a href="/login" className="text-xs font-semibold text-slate-500 hover:text-[#1A1A1A]">
            {t("supplierReg.alreadyHaveAccount")} <span className="underline">{t("supplierReg.signIn")}</span>
          </a>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <span className="inline-block bg-[#FFDF00] text-[#1A1A1A] px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold mb-3">
            Paso {step} de {codeOfConduct ? 6 : 5}
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#1A1A1A]">{t("supplierReg.pageTitle")}</h1>
          <p className="text-slate-500 mt-2 leading-relaxed">{t("supplierReg.pageSubtitle")}</p>
        </motion.div>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5">
          <AnimatePresence mode="popLayout" initial={false}>
            {step === 1 && (
              <motion.div key="s0type" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierReg.typeStepTitle")}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { v: "insumos", icon: Package, title: t("supplierReg.typeInsumos"), hint: t("supplierReg.typeInsumosHint") },
                    { v: "servicios", icon: Wrench, title: t("supplierReg.typeServicios"), hint: t("supplierReg.typeServiciosHint") },
                  ].map((opt) => {
                    const selected = form.supplier_type === opt.v;
                    return (
                      <button key={opt.v} type="button" data-testid={`reg-type-${opt.v}`}
                        onClick={() => setForm({ ...form, supplier_type: opt.v, category: "" })}
                        className={`text-left p-5 rounded-2xl border-2 transition ${
                          selected ? "border-[#FFDF00] bg-[#FFFBE6]" : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}>
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${
                          selected ? "bg-[#FFDF00] text-[#1A1A1A]" : "bg-slate-100 text-slate-600"
                        }`}>
                          <opt.icon className="w-6 h-6" strokeWidth={2} />
                        </div>
                        <div className="font-display font-bold text-base text-[#1A1A1A] flex items-center gap-2">
                          {opt.title}
                          {selected && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                        </div>
                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{opt.hint}</p>
                      </button>
                    );
                  })}
                </div>
                <button data-testid="reg-step0-next" onClick={() => setStep(2)}
                  disabled={!form.supplier_type}
                  className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                  {t("common.next")} <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierReg.taxData")}</h2>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.businessName")}</label>
                  <input data-testid="reg-business-name" value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    placeholder={t("supplierReg.businessNamePh")}
                    className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.rfc")}</label>
                    <input data-testid="reg-rfc" value={form.rfc}
                      onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                      placeholder="ABC123456XY7" maxLength={13}
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.categoryHint")}</label>
                    <input data-testid="reg-category" value={form.category} list="reg-category-options"
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      placeholder={isServicios ? t("supplierReg.categoryServiciosPh") : t("supplierReg.categoryInsumosPh")}
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                    <datalist id="reg-category-options">
                      {(CATEGORY_SUGGESTIONS[form.supplier_type] || []).map((c) => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.address")}</label>
                  <input data-testid="reg-address" value={form.tax_address}
                    onChange={(e) => setForm({ ...form, tax_address: e.target.value })}
                    placeholder={t("supplierReg.addressPh")}
                    className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep(1)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("supplierReg.back")}</button>
                  <button data-testid="reg-step1-next" onClick={() => setStep(3)}
                    disabled={!form.business_name || !form.rfc || !form.tax_address}
                    className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                    {t("common.next")} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierReg.contactPayments")}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.emailRequired")}</label>
                    <input data-testid="reg-email" type="email" value={form.contact_email}
                      onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                      placeholder="ventas@miempresa.mx"
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.phoneRequired")}</label>
                    <input data-testid="reg-phone" value={form.contact_phone}
                      onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                      placeholder={t("supplierReg.phonePh")}
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.bank")}</label>
                    <input data-testid="reg-bank-name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                      placeholder={t("supplierReg.bankPh")}
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.clabe")}</label>
                    <input data-testid="reg-bank-account" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                      maxLength={18} placeholder="012180000000000000"
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.creditDays")}</label>
                  <input type="number" min="0" max="120" value={form.credit_days}
                    onChange={(e) => setForm({ ...form, credit_days: parseInt(e.target.value) || 0 })}
                    className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                </div>

                {isServicios && (
                  <div className="space-y-4 pt-2 border-t border-slate-100" data-testid="reg-service-fields">
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.serviceModality")} <span className="text-red-600">*</span></label>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {MODALITY_OPTIONS.map((m) => (
                          <label key={m} data-testid={`reg-modality-${m}`}
                            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer text-sm ${
                              form.service_modality === m ? "border-[#FFDF00] bg-[#FFFBE6] font-semibold" : "border-slate-200 hover:bg-slate-50"
                            }`}>
                            <input type="radio" name="service_modality" value={m} checked={form.service_modality === m}
                              onChange={() => setForm({ ...form, service_modality: m })}
                              className="w-4 h-4 text-[#FFDF00] focus:ring-[#FFDF00]" />
                            {t(`supplierReg.modalityOptions.${m}`)}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.serviceResponseTime")} <span className="text-red-600">*</span></label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {RESPONSE_OPTIONS.map((rt) => (
                          <label key={rt} data-testid={`reg-response-${rt}`}
                            className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-sm ${
                              form.service_response_time === rt ? "border-[#FFDF00] bg-[#FFFBE6] font-semibold" : "border-slate-200 hover:bg-slate-50"
                            }`}>
                            <input type="radio" name="service_response_time" value={rt} checked={form.service_response_time === rt}
                              onChange={() => setForm({ ...form, service_response_time: rt })}
                              className="w-4 h-4 text-[#FFDF00] focus:ring-[#FFDF00]" />
                            {t(`supplierReg.responseTimeOptions.${rt}`)}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <button onClick={() => setStep(2)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("supplierReg.back")}</button>
                  <button data-testid="reg-step2-next" onClick={() => setStep(4)}
                    disabled={!form.contact_email || !form.contact_phone || !serviceFieldsOk}
                    className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                    {t("common.next")} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s3docs" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierReg.docsTitle")}</h2>
                <p className="text-sm text-slate-500">{t("supplierReg.docsSubtitle")}</p>
                <div className="space-y-3">
                  {DOC_FIELDS.map((f) => {
                    const st = docs[f.type];
                    return (
                      <div key={f.type} className="border border-slate-200 rounded-xl p-3.5" data-testid={`reg-doc-${f.type}`}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="text-sm font-semibold text-[#1A1A1A]">
                            {t(`supplierReg.docLabels.${f.type}`)}
                            {f.required
                              ? <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-red-600">{t("supplierReg.docRequired")}</span>
                              : <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">{t("supplierReg.docOptional")}</span>}
                          </div>
                          {st?.id && <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
                        </div>
                        {f.hint && <p className="text-xs text-slate-500 mb-2">{t(`supplierReg.docHints.${f.type}`)}</p>}
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold">
                            <Upload className="w-3.5 h-3.5" /> {t("supplierReg.chooseFile")}
                          </span>
                          <span className="text-xs text-slate-500 truncate">
                            {st?.uploading ? t("supplierReg.uploading") : (st?.file?.name || t("supplierReg.noFile"))}
                          </span>
                          <input data-testid={`reg-doc-input-${f.type}`} type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden"
                            onChange={(e) => handleDocChange(f.type, e.target.files?.[0])} />
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep(3)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("supplierReg.back")}</button>
                  <button data-testid="reg-step3-next" onClick={() => setStep(5)}
                    disabled={!mandatoryUploaded}
                    className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                    {t("common.next")} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div key="s4" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierReg.ndaTitle")}</h2>
                <div className="bg-slate-50 rounded-xl p-4 max-h-64 overflow-y-auto text-xs leading-relaxed text-slate-700 space-y-2">
                  <p><strong>{t("supplierReg.ndaTitle")} — Chizy Chiz Pizza</strong></p>
                  <p>{t("supplierReg.ndaBody1")}</p>
                  <p>{t("supplierReg.ndaBody2")}</p>
                  <p>{t("supplierReg.ndaBody3")}</p>
                  <p>{t("supplierReg.ndaBody4")}</p>
                  <p><Trans i18nKey="supplierReg.ndaBody5" components={{ strong: <strong /> }} /></p>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input data-testid="reg-nda-check" type="checkbox" checked={form.nda_accepted}
                    onChange={(e) => setForm({ ...form, nda_accepted: e.target.checked })}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#FFDF00] focus:ring-[#FFDF00]" />
                  <span className="text-sm text-slate-700">
                    <Trans i18nKey="supplierReg.iAgree" components={{ strong: <strong /> }} />
                  </span>
                </label>
                <div className="bg-[#FFFBE6] border border-[#FFDF00] rounded-xl p-3 flex items-start gap-2 text-xs text-slate-700">
                  <ShieldCheck className="w-4 h-4 text-[#1A1A1A] flex-shrink-0 mt-0.5" />
                  <span>{t("supplierReg.ndaShield")}</span>
                </div>
                {privacyNotice && (
                  <label className="flex items-start gap-3 cursor-pointer border-t border-slate-100 pt-4">
                    <input data-testid="reg-privacy-check" type="checkbox" checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#FFDF00] focus:ring-[#FFDF00]" />
                    <span className="text-sm text-slate-700">
                      {t("supplierReg.privacyLabel", { n: privacyNotice.version_number, date: privacyNotice.effective_date })}{" "}
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${privacyNotice.pdf_url}`} target="_blank" rel="noopener noreferrer"
                        data-testid="reg-privacy-link" className="font-semibold text-[#1A1A1A] underline">
                        {t("supplierReg.privacyView")}
                      </a>
                    </span>
                  </label>
                )}
                <div className="flex gap-2">
                  <button onClick={() => setStep(4)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("supplierReg.back")}</button>
                  {codeOfConduct ? (
                    <button data-testid="reg-step5-next"
                      onClick={() => setStep(6)}
                      disabled={!form.nda_accepted || (privacyNotice && !privacyAccepted)}
                      className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                      {t("common.next")} <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button data-testid="reg-submit" onClick={submit} disabled={submitting || !form.nda_accepted || (privacyNotice && !privacyAccepted)}
                      className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                      {submitting ? t("supplierReg.sending") : t("supplierReg.submit")}
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {step === 6 && codeOfConduct && (
              <motion.div key="s5code" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">Código de Conducta para Proveedores</h2>
                <p className="text-sm text-slate-500">
                  Versión {codeOfConduct.version_number} · vigente desde {codeOfConduct.effective_date}
                </p>
                <object
                  data={`${process.env.REACT_APP_BACKEND_URL}${codeOfConduct.pdf_url}`}
                  type="application/pdf"
                  data-testid="reg-code-pdf"
                  className="w-full h-80 rounded-xl border border-slate-200 bg-slate-50"
                >
                  <div className="p-6 text-sm text-slate-600 text-center">
                    Tu navegador no puede mostrar el PDF aquí.{" "}
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${codeOfConduct.pdf_url}`} target="_blank" rel="noopener noreferrer"
                      className="font-semibold underline">Descargar para leer</a>
                  </div>
                </object>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${codeOfConduct.pdf_url}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A1A1A] hover:underline">
                  Descargar PDF
                </a>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input data-testid="reg-code-check" type="checkbox" checked={codeAccepted}
                    onChange={(e) => setCodeAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#FFDF00] focus:ring-[#FFDF00]" />
                  <span className="text-sm text-slate-700">
                    He leído y acepto el Código de Conducta para Proveedores de Chizy Chiz (versión {codeOfConduct.version_number}, vigente desde {codeOfConduct.effective_date}).
                  </span>
                </label>
                <div className="flex gap-2">
                  <button onClick={() => setStep(5)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">
                    {t("supplierReg.back")}
                  </button>
                  <button data-testid="reg-submit" onClick={submit}
                    disabled={submitting || !codeAccepted}
                    className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                    {submitting ? t("supplierReg.sending") : t("supplierReg.submit")}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
